import { nextFrame } from "../../../library-js/animation/nextFrame";
import { sleep } from "../../../library-js/animation/sleep";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { CombinedSelector } from "../../../library-js/dom-tools/CombinedSelector";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementSelector } from "../../../library-js/dom-tools/ElementSelector";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnClick } from "../../../library-js/dom-tools/EventListeners";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { Link } from "../../../library-js/dom-tools/LinkResolver";
import { Request } from "../../../library/ts/html/server/Request";
import { SelectedScriptLanguage } from "../../js/cookie-settings/DocSearchSettings";
import { GodotDocsUpdateData, GodotDocsUpdater } from "../../js/godot-docs/GodotDocsUpdate";
import { Rokojori } from "../../js/Rokojori";
import { ClassInfo } from "./ClassInfo";
import { DocSearch } from "./DocSearch";
import { GodotSource } from "./GodotSource";
import { GodotSourceLibrary } from "./GodotSourceLibrary";
import { MemberInfo } from "./MemberInfo";
import { MembersInfo } from "./MembersInfo";
import { GodotInspectorViewCreator } from "../../templates/godot/godot-inspector/GodotInspectorViewCreator";

export class CodeInfo
{
  static type = new ElementType( "code-info" );
  static container = new ElementType( "code-info-container" );
  static description = new ClassFlag( "description" );

  static originalMemberName = new ElementAttribute( "orginal-member-name" );
  static lowerCaseMemberName = new ElementAttribute( "lowercase-member-name" );

  static updateFunction:()=>void = null;
  static hasElement = false;
  static lastHash:string = null;

  static gdScript = new ElementSelector( ElementType.div, new ClassFlag( "highlight-gdscript" ) );
  static csScript = new ElementSelector( ElementType.div, new ClassFlag( "highlight-csharp" ) );
  static tabPanel = new ElementSelector( ElementType.div, new ClassFlag( "sphinx-tabs-panel" ) );
  static codeBlocks = new ElementType( "gd-doc-codeblocks" );

  static applyOnDocument()
  {
    if ( this.updateFunction )
    {
      Rokojori.$.onAnimationFrame.removeListener( this.updateFunction );
      
    }
    
    CodeInfo.lastHash = null;
    CodeInfo.hasElement = false;
    CodeInfo.type.forAllInDoc( ci => CodeInfo.applyOnElement( ci ) );


  }

  static readonly url = "_php/docs/load-docs.php";

  static async loadUpdatedDoc( e:Element, sourcePath:string  )
  {
    if ( e == null )
    {
      return;
    }

    await sleep( 200 );

    let hash = window.location.hash;

    console.log( "HASH", `"${hash}"` );

    if ( hash === '#set-editing-key' )
    {
      let key = new URLSearchParams( window.location.search ).get( "key" );
      GodotDocsUpdater.applyOnElement( e );
      Rokojori.$.changeCookieSettings( cs => { cs.editing = key; } );
    }
    else if ( Rokojori.$.cookieSettings.editing )
    {
      GodotDocsUpdater.applyOnElement( e );
    }
    else
    {
      setTimeout(
        ()=>
        {
          if ( Rokojori.$.cookieSettings.editing )
          {
            GodotDocsUpdater.applyOnElement( e );
          }
        }, 
        500
      );

      console.log( "Not editable", window.location.hash );
    }
   

    let link = Link.Root( "" );
    let relativeURL = Rokojori.$.pageHandler.createRelativeLink( link ).path + CodeInfo.url;
    let data = 
    {
      sourcePath: sourcePath,
      version: "4.4"
    }

    let result = await Request.post<any,GodotDocsUpdateData>( relativeURL, data );

    if ( ( result as any ).error )
    {
      return;
    }

    // console.log( "result:", result );

    let members = MemberInfo.type.queryAll( e );

    result.content.forEach(
      ( c )=>
      {
        let target = c.target;
        let contentElement = e;

        if ( target.indexOf( GodotDocsUpdater.delimiter ) != -1 )
        {
          let splits =target.split( GodotDocsUpdater.delimiter );
          target = splits[ 1 ];
          let originalName = splits[ 0 ];
          contentElement = members.find( m => GodotDocsUpdater.getTargetName( m ) == originalName );
        }

        let targetType = new ElementType( target );
        let targetElement = targetType.query( contentElement );

        if ( targetElement == null )
        {
          console.log( "No target found:", target, c );
          return;
        }

        targetElement.innerHTML = c.content;

        HTMLEditor.forAll( targetElement, "a.reference.internal", ( a ) => 
          {
            DocSearch.createDocLink( a as HTMLAnchorElement );
          }
          );
      }
    );

    this.addInspectorView( ClassInfo.classDescription.query( e ) );
    
  }

  static setLinkedName( el:Element, name:string )
  {
    let memberInfo = MemberInfo.type.getParent( el );

    if ( ! MemberInfo.memberType.in( memberInfo ) )
    {
      console.log( "No member type:", el );
    }

    let type = MemberInfo.memberType.from( memberInfo, "property" ).toLowerCase().replace( " ", "-" );
    let link = CodeInfo.originalMemberName.from( el ).toLowerCase().replace( " ", "-" );

    el.innerHTML = "";
    let anchor = ElementType.anchor.create( name );
    ElementAttribute.href.to( anchor, "#" + type + "-" + link );
    el.appendChild( anchor );
  }


  static applyOnElement( e:Element ) 
  {

    CodeInfo.hasElement = true; 

    if ( ! this.updateFunction )
    {
      CodeInfo.lastHash = null;
      this.updateFunction = ()=>
      {
        let hash = window.location.hash || "";
        hash = hash.replace( /^#/, "" );

        if ( CodeInfo.lastHash !== hash )
        {
          let regex = /(property|method|accessor-get|accessor-set)-((?:\w+))/i; 
          CodeInfo.lastHash = hash;

          let result = regex.exec( CodeInfo.lastHash );

          if ( !result )
          {
            return;
          }
          
          let type = result[ 1 ].replace( "-", " " );
          let lcName = result[ 2 ].toLowerCase();
          let selector = `member-info[data-type="${type}"] member-name[${CodeInfo.lowerCaseMemberName}="${lcName}"]`;
          let selected = e.querySelector( selector );

          console.log( "#", result, hash, selector, selected );
          
          if ( selected )
          {
            selected.scrollIntoView( { behavior: "smooth" } );
          }
          
        }
      };

      ( 
        async ()=>
        {
          await nextFrame();        
          Rokojori.$.onAnimationFrame.addListener( this.updateFunction );
        }

      )()
     
    }

    let className = ClassInfo.className.query( e );
    let classInheritance = ClassInfo.classInheritance.query( e );
    let classInterfaces = ClassInfo.classInterfaces.query( e );

    if ( classInterfaces )
    {
      let anchors = ElementType.anchor.queryAll( classInterfaces );
      anchors.forEach( p => DocSearch.processType( p.childNodes[ 0 ], true ) );
    }

    let sourcePath = ClassInfo.sourcePath.from( className );

    if ( sourcePath != null && sourcePath != "" )
    {
      
      sourcePath = sourcePath.replace( /\/\//g, "\/" );

      this.loadUpdatedDoc( e, sourcePath );
      
      let anchor = ElementType.anchor.create( "🡵 " + sourcePath );

      let rokojoriSourceURL = "https://community.rokojori.com/Rokojori/rj-action-library/src/branch/main/";
      ElementAttribute.href.to( anchor, rokojoriSourceURL + sourcePath );
      ElementAttribute.target.to( anchor, "_blank" );

      let sourcePathElement = ClassInfo.sourcePathElement.create( anchor);
      HTMLEditor.insertBefore( classInheritance.parentElement, sourcePathElement, classInheritance  );      

    }
    else if ( GodotSource.hasType( className.textContent ) )
    {
      sourcePath = GodotSource.getTypePath( className.textContent );
      let anchor = ElementType.anchor.create( "🡵 " + sourcePath );

      let godotSourceURL = "https://github.com/godotengine/godot/tree/master/";
      ElementAttribute.href.to( anchor, godotSourceURL + sourcePath );
      ElementAttribute.target.to( anchor, "_blank" );

      let anchor2 = ElementType.anchor.create( "🡵 " + sourcePath.replace( /.h$/, ".cpp") );

      ElementAttribute.href.to( anchor2, godotSourceURL + sourcePath.replace( /.h$/, ".cpp") );
      ElementAttribute.target.to( anchor2, "_blank" );
      
      let space = ElementType.span.create( " | " );
      
      let children = [ anchor, space, anchor2 ];

      if ( GodotSource.isCore( className.textContent )  )
      {
        let csSourcePath = GodotSource.getCSCoreTypePath( className.textContent );
        let anchor3 = ElementType.anchor.create( "🡵 " + csSourcePath );

        ElementAttribute.href.to( anchor3, godotSourceURL + csSourcePath );
        ElementAttribute.target.to( anchor3, "_blank" );
        
        children = [ anchor, ElementType.span.create( " | " ), anchor2, ElementType.span.create( " | " ), anchor3 ];
      }

      let sourcePathElement = ClassInfo.sourcePathElement.create( children );
      HTMLEditor.insertBefore( classInheritance.parentElement, sourcePathElement, classInheritance  );

     

      // let sourcePathElement2 = ClassInfo.sourcePathElement.create( anchor2 );
      // HTMLEditor.insertBefore( classInheritance.parentElement, sourcePathElement2, classInheritance  );
    }
    else
    {
      // console.log( "Not found:", `"${className.textContent}"`)
    }

    let membersInfo = MembersInfo.type.query( e );  
    let memberInfos = MemberInfo.type.queryAll( membersInfo );

    HTMLEditor.forAll( e, "a.reference.internal", ( a ) => 
    {
      
      DocSearch.createDocLink( a as HTMLAnchorElement );
    }
    );

    // HTMLEditor.forAll( e, "a", ( a ) => 
    //   {
    //     if (
    //        ! ElementAttribute.href.in( a ) ||
    //        ElementAttribute.href.from( a ).startsWith( "http" )
    //     )
    //     {
    //       return;
    //     }

    //     console.log( as )

    //     DocSearch.createDocLink( a as HTMLAnchorElement );
    //   }
    //   );

    if ( Rokojori.$.cookieSettings.ds.exp )
    {
      MemberInfo.exportedMembersOnly.to( membersInfo );
    }

    
    let isGodotType = GodotSource.hasType( className.textContent );

    memberInfos.forEach(
      ( mi )=>
      {
        
        let memberAttributes = MemberInfo.memberAttribute.queryAll( mi );
        
      
        memberAttributes.forEach(
          ( att )=>
          {
            let value = MemberInfo.memberAttributeValue.from( att );

            if ( value.startsWith( "Export" ) )
            {
              MemberInfo.exportedMember.to( mi );
            }
          }
        );

        let dataType = ElementAttribute.dataType.from( mi );

        if ( isGodotType && /property|signal/.test( dataType ) )
        {
          MemberInfo.exportedMember.to( mi );
        }
        
        let memberDataType = MemberInfo.dataType.query( mi ).textContent.trim();

        if ( ! isGodotType && /^(SequenceAction|Action)$/.test( memberDataType ) )
        {
          MemberInfo.actionMember.to( mi );
        }

        let memberHeader = MemberInfo.memberHeader.query( mi );
        let nameElement = MemberInfo.memberName.queryDirectChild( memberHeader );
        let name = nameElement.textContent;

       
        // nameElement.innerHTML = "";

        // let linkedName = ElementType.anchor.create( name );
        // ElementAttribute.href.to( linkedName, "#"+ name );
        // nameElement.appendChild( linkedName );

               

        CodeInfo.originalMemberName.to( nameElement, name );        
        CodeInfo.lowerCaseMemberName.to( nameElement, name.toLowerCase() );     
        
        // this.setLinkedName( nameElement, name );

        let description = MemberInfo.description.query( mi );



        // OnClick.add( memberHeader as HTMLElement,
        //   ( e )=>
        //   {
        //     let isDataType = MemberInfo.dataType.is( e.target as HTMLElement );

        //     if ( isDataType )
        //     {
        //       let dataType = e.target as HTMLElement;
        //       let dataTypeContent = ( dataType.textContent + "" ).replace( /^\s*\:\s*/, "" );
              
        //       console.log( dataTypeContent );
        //     }
        //     // console.log( isDataType );
   
                        
            
        //   }
        // );
        
        
        if ( description.textContent.trim() === "" )
        {
          description.innerHTML = "";
        }
        else
        {
          let openInfo = "+";
          let closeInfo  ="−";
          let button = ElementType.button.create( openInfo );

          CodeInfo.description.set( button );

          let title = MemberInfo.title.query( mi );

          let titleElements = ElementType.any.queryDirectChildren( title );

          // console.log( "Num titles:", titleElements );

          let target:Element = title;

          if ( titleElements.length === 1 )
          {
            target = title.childNodes[ 0 ] as Element;
          } 

          target.appendChild( document.createTextNode( " " ) );
          target.appendChild( button );
           

          OnClick.add( button as HTMLElement, 
            ( ce )=>
            {
              ClassFlag.visible.toggle( description );
              button.innerHTML = ClassFlag.visible.in( description ) ? closeInfo : openInfo;
            }
          );
        }


        // mi.innerHTML = mi.innerHTML.replace( /<\/?p>/g, "" ); 
      }
    );
    

    this.activateTabs( e );

    this.addInspectorView( ClassInfo.classDescription.query( e ) );
    
    Rokojori.$.onCookieSettings(
      ( cs )=>
      {
        let ds = cs.ds;

        let membersInfo = MembersInfo.type.query( e );  

        if ( Rokojori.$.cookieSettings.ds.exp )
        {
          MemberInfo.exportedMembersOnly.to( membersInfo );
        }
        else
        {
          MemberInfo.exportedMembersOnly.removeFrom( membersInfo );
        }
      
        CodeInfo.setScriptLanguage( 
          SelectedScriptLanguage.CSharp === ds.t ? SelectedScriptLanguage.CSharp : 
          SelectedScriptLanguage.Editor === ds.t ? SelectedScriptLanguage.Editor :
          SelectedScriptLanguage.GDScript
        );
      }
    );
    
  }

  static addInspectorView( classDescription:Element )
  {
    if ( classDescription == null || true )
    {
      console.log( "INSPECTOR VIEW: No class description" );
      return;
    }

    let codeInfo = CodeInfo.type.getParent( classDescription );
    let isCS = ClassInfo.isCSOnly.in( ClassInfo.className.query( codeInfo ) );

    if ( ! isCS )
    {
      return;
    }

    console.log( "INSPECTOR VIEW:", classDescription );
    
    let inspectorView = GodotInspectorViewCreator.fromDoc( codeInfo );

    let allChildren = HTMLEditor.removeChildren( classDescription );
    let classDescriptionInfo = ClassInfo.classDescriptionInfo.create( allChildren );
     
    let splitView = ClassInfo.classDescriptionSplitView.create(
      [
        classDescriptionInfo,
        inspectorView
      ]
    );

    classDescription.appendChild( splitView );

    // Rokojori.$.processRawTemplateElement( inspectorView );


    // classDescription.innerHTML = "";

    // console.log( "INSPECTOR VIEW:", classDescription );
    
 
  }


  static setScriptLanguage( type:SelectedScriptLanguage, retry:boolean = true )
  {
    let e = CodeInfo.type.queryDoc();
    let elements = CodeInfo.originalMemberName.queryDocAll();

    // console.log( "Set Script Lang", e );
    let classInfo = ClassInfo.type.query( e );
    let className = ClassInfo.className.query( classInfo );

    let languageButtons  = CombinedSelector.create( DocSearch.outlineContainer, DocSearch.languageFlag ).queryAll( document.documentElement ) as HTMLElement[];
    languageButtons.forEach( b => b.style.display = "inline" );

    // console.log( "lb", languageButtons  );

    if ( languageButtons == null || languageButtons.length == 0 )
    {
      if ( retry )
      {
        setTimeout(
          ()=>
          {
            CodeInfo.setScriptLanguage( type, false );
          },
          10
        );

        setTimeout(
          ()=>
          {
            CodeInfo.setScriptLanguage( type, false );
          },
          20
        );

        setTimeout(
          ()=>
          {
            CodeInfo.setScriptLanguage( type, false );
          },
          100
        );

        setTimeout(
          ()=>
          {
            CodeInfo.setScriptLanguage( type, false );
          },
          1000
        );

      }


      return;

    }

    let buttonGroup = DocSearch.buttonGroup.getParent( languageButtons[ 0 ] ) as HTMLElement; 

    buttonGroup.style.display = "inline-block";
    
    let isCS = ClassInfo.isCSOnly.in( className );

    console.log( "isCS", isCS );
    // if ( ClassInfo.isCSOnly.in( className ) )
    // {
    //   type = SelectedScriptLanguage.GDScript;
    //   languageButtons.forEach( b => b.style.display = "none" );
    //   buttonGroup.style.display = "none";
    // }
    

    elements.forEach(
      ( el )=>
      {
        let originalMemberName = CodeInfo.originalMemberName.from( el );

        let originalMemberInfo =  MemberInfo.type.getParent( el );
        let isAccessor = ( ElementAttribute.dataType.from( originalMemberInfo ) || "" ).indexOf( "accessor" ) != -1; 

        if ( isAccessor && isCS )
        {
          originalMemberName = originalMemberName.replace( /^(set_|get_)/, "");
        }

        // console.log( originalMemberName, "isAccessor", isAccessor, el, originalMemberInfo );

        let name = this.getScriptName( originalMemberName, type, isCS );

        this.setLinkedName( el, name );
        // HTMLEditor.setText( el, name );
        
        // el.innerHTML = "";
        // let anchor = ElementType.anchor.create( name );
        // ElementAttribute.href.to( anchor, "#" + originalMemberName );
        // el.appendChild( anchor );

        // HTMLEditor.setText( el, name );
      }
    );

    CodeInfo.gdScript.forAllIn( e, 
      ( d )=>
      { 
        let targetElement = d.parentElement;

        if ( !  CodeInfo.tabPanel.selects( targetElement )  )   
        {
          if ( CodeInfo.codeBlocks.is( targetElement ) )
          {
            targetElement = d;
          }
          else
          {
            return;
          }
          
        }

        ElementAttribute.hidden.toggleRemove( targetElement, SelectedScriptLanguage.GDScript !== type );
      }
    );

    CodeInfo.csScript.forAllIn( e, 
      ( d )=>
      { 
        let targetElement = d.parentElement;

        if ( !  CodeInfo.tabPanel.selects( targetElement )  )   
        {
          if ( CodeInfo.codeBlocks.is( targetElement ) )
          {
            targetElement = d;
          }
          else
          {
            return;
          }
          
        }

        ElementAttribute.hidden.toggleRemove( targetElement, SelectedScriptLanguage.CSharp !== type );
      }
    );
  }

  
  static activateTabs( e:Element )
  {
    HTMLEditor.forAll( e, "div.sphinx-tabs", el => CodeInfo.processTabs( el ) );
  }

  static processTabs( tab:Element )
  {
    
    let buttons = ElementType.button.queryAll( tab );
    let allTargets = buttons.map( b => tab.querySelector( "[id='" + b.getAttribute( "aria-controls" ) + "']" ) );

    // console.log( "processTabs", tab, buttons, allTargets );

    buttons.forEach(
      ( b )=>
      {
        OnClick.add( b,
          ( ce )=>
          {
            allTargets.forEach( t => ElementAttribute.hidden.to( t, true ) );
            let target = tab.querySelector( "[id='" + b.getAttribute( "aria-controls" ) + "']" )
    
            ElementAttribute.hidden.removeFrom( target );
          }
        );
       
      }
    );
  }

  static readonly csharp = "csharp";
  static readonly gdscript = "gdscript";
  static readonly editor = "editor";

  static isUpperCase( source:string )
  {
    return source == source.toUpperCase();
  }

  static needsWhiteSpace( source:string, index:number )
  {
    if ( index == 0 )
    {
      return false;
    }

    if ( source[ index ] == "_" )
    {
      return source[ index - 1 ] != "_";
    } 

    // #B#
    if ( this.isUpperCase( source[ index ] ) )
    {
      
      // aB#
      if ( index > 0 && ! this.isUpperCase( source[ index - 1 ] ) )
      {
        // aB# => a B#
        return true;
      }
      
      // ABc
      if ( index + 1 < source.length && ! this.isUpperCase( source[ index + 1 ] ) )
      {
        // ABc => a Bc
        return true;
      }
      
    }

    return false;
  }

  static shouldBeUpperCase( source:string, index:number )
  {
    if ( index == 0 )
    {
      return true;
    }

    if ( source[ index - 1 ] == "_" )
    {
      return true;
    }

    return this.isUpperCase( source[ index ] ) && ! this.isUpperCase( source[ index - 1 ] );
  }

  static getEditorScriptName( memberName:string )
  {
    let output = "";

    for ( let i = 0; i < memberName.length; i++ )
    {
      let needsWhiteSpace = this.needsWhiteSpace( memberName, i );

      if ( needsWhiteSpace )
      {
        output += " ";
      }
      
      if ( memberName[ i ] == "_" )
      {
        continue;
      }

      let char = memberName[ i ].toLowerCase();

      if ( this.shouldBeUpperCase( memberName, i ) || needsWhiteSpace )
      {
        char = char.toUpperCase();
      }

      output += char;
    }

    return output;
  }

  static getScriptName( memberName:string, type:SelectedScriptLanguage, isCS:boolean )
  {
    if ( type == SelectedScriptLanguage.Editor )
    {
      // console.log( "getScriptName:", memberName );
      try
      {
        let output = this.getEditorScriptName( memberName );
        return output;
      }
      catch( e) 
      {
        console.log(e );
        return memberName;
      }
    } 

    if ( isCS )
    {
      return memberName;
    }

    if ( type === SelectedScriptLanguage.CSharp )
    {
      let prefix = memberName.startsWith( "_" ) ? /^\_+/.exec( memberName )[ 0 ] : "";
      let cachedName = memberName.replace( /^\_+/, "" ) ;
      let csharpName = cachedName[ 0 ].toUpperCase();      

      for ( let i = 1; i < cachedName.length; i++ )
      {
        if ( cachedName[ i ] === "_" )
        {
          i++;

          csharpName += cachedName[ i ].toUpperCase();
        }
        else
        {
          csharpName += cachedName[ i ];
        }
      }

      return prefix + csharpName;
    }

    return memberName;
  }
}